import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import BlogHeader from "./BlogHeader";
import Footer from "./Footer";
import { useSeo } from "../lib/seo";

export default function ServicePage({ seo, kicker, h1, lead, features, priceTitle, priceDesc, faqs, related }) {
  useSeo({
    title: seo.title,
    description: seo.description,
    path: seo.path,
    jsonLd: faqs?.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null,
  });

  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <BlogHeader />
      <main>
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mono-label text-[#E05638] mb-6">{kicker}</p>
            <h1 className="font-display font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              {h1}
            </h1>
            <p className="mt-8 text-neutral-600 text-base sm:text-lg leading-relaxed max-w-2xl">{lead}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                data-testid="service-cta-quote"
                href="/#contatti"
                className="inline-flex items-center gap-2 rounded-full bg-[#111111] text-white px-8 py-4 text-sm font-semibold hover:bg-[#E05638] transition-colors duration-300"
              >
                Richiedi un preventivo <ArrowRight size={16} />
              </a>
              <a
                data-testid="service-cta-aid"
                href="/#agevolazioni"
                className="inline-flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 text-sm font-semibold hover:border-[#E05638] hover:text-[#E05638] transition-colors duration-300"
              >
                Sei una piccola attività?
              </a>
            </div>
          </motion.div>
        </section>

        <section className="bg-white border-y border-black/5 py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <p className="mono-label text-neutral-500 mb-10">Cosa include</p>
            <div className="grid sm:grid-cols-2 gap-x-12 gap-y-6">
              {features.map((f) => (
                <motion.div
                  key={f}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5 }}
                  className="flex items-start gap-4"
                >
                  <span className="w-7 h-7 rounded-full bg-[#E05638]/10 text-[#E05638] grid place-items-center shrink-0 mt-0.5">
                    <Check size={14} />
                  </span>
                  <p className="text-sm sm:text-base text-neutral-700 leading-relaxed">{f}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <div className="rounded-2xl bg-[#111111] text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center gap-10">
            <div className="flex-1">
              <p className="mono-label text-[#E05638] mb-4">Investimento</p>
              <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">{priceTitle}</h2>
              <p className="mt-4 text-neutral-400 text-sm sm:text-base leading-relaxed max-w-xl">{priceDesc}</p>
            </div>
            <a
              data-testid="service-cta-price"
              href="/#piani"
              className="inline-flex items-center gap-2 rounded-full bg-[#E05638] text-white px-8 py-4 text-sm font-semibold hover:bg-[#C8452A] transition-colors duration-300 shrink-0"
            >
              Vedi i piani <ArrowRight size={16} />
            </a>
          </div>
        </section>

        {faqs?.length > 0 && (
          <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-20">
            <p className="mono-label text-neutral-500 mb-10">Domande frequenti</p>
            <div className="space-y-4">
              {faqs.map((f) => (
                <details key={f.q} className="group rounded-xl bg-white border border-black/10 px-6 py-5">
                  <summary className="font-display font-bold text-base sm:text-lg cursor-pointer list-none flex items-center justify-between gap-4">
                    {f.q}
                    <span className="text-[#E05638] text-2xl leading-none group-open:rotate-45 transition-transform duration-300">+</span>
                  </summary>
                  <p className="mt-4 text-sm text-neutral-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {related?.length > 0 && (
          <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
            <p className="mono-label text-neutral-500 mb-8">Scopri anche</p>
            <div className="grid sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.path}
                  to={r.path}
                  data-testid={`service-related-${r.path.slice(1)}`}
                  className="group rounded-xl bg-white border border-black/10 p-6 hover:border-[#E05638] transition-colors duration-300"
                >
                  <p className="font-display font-bold text-base group-hover:text-[#E05638] transition-colors">{r.label}</p>
                  <p className="text-xs text-neutral-500 mt-2 flex items-center gap-1.5">
                    Scopri di più <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
